import { useMemo, useState } from 'react'
import { Button } from '~/shadcn/ui/button/button'
import type { IAccountAmbassadorData } from '~/@api/generated-api-types-ambassador/Api'
import { CurrencyFormatter } from '~/@mid_ui/currency-formatter/CurrencyFormatter'
import { GraphIcon } from '~/@mid_ui/icons/GraphIcon'
import { useIsDesktop } from '~/@mid_ui/layout/useIsDesktop'
import { HeadingThree } from '~/@mid_ui/typography/HeadingThree'
import { ExportDialog } from '~/@biz_ambassador/export/ExportDialog'
import { useAmbassadorExport } from '~/@biz_ambassador/export/useAmbassadorExport'
import { AmbassadorsTable } from './AmbassadorsTable'
import type { MyAmbassadorItem } from './AmbassadorsTable'

type Props = {
  data: IAccountAmbassadorData | undefined
  isLoading: boolean
  setIsReportDialogOpen: (open: boolean) => void
  setIsCreateDialogOpen: (open: boolean) => void
  onSelectAmbassador: (item: MyAmbassadorItem) => void
}

export const Dashboard = ({
  data,
  isLoading,
  setIsReportDialogOpen,
  setIsCreateDialogOpen,
  onSelectAmbassador,
}: Props) => {
  const [isExportDialogOpen, setIsExportDialogOpen] = useState(false)

  const isDesktop = useIsDesktop()

  const firstMonth = useMemo(() => {
    if (data == null) return null

    return data?.ambassadors?.length > 0
      ? [...data.ambassadors].sort(
          (a, b) =>
            new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
        )[0].createdAt
      : null
  }, [data])

  const { months, downloadingKey, handleExportGlobal } =
    useAmbassadorExport(firstMonth)

  const items: Array<MyAmbassadorItem> = useMemo(
    () =>
      data?.ambassadors.map((a) => ({
        id: a._id,
        name: a.reference,
        clients: a.sums.clientCount,
        invested: a.sums.investedSum,
        code: a.code,
        active: a.isEnabled,
        createdAt: a.createdAt,
      })) ?? [],
    [data?.ambassadors]
  )

  return (
    <>
      <div className="flex flex-row justify-between items-center">
        {isDesktop && <HeadingThree>Leader ambasádor dashboard</HeadingThree>}
        <Button
          variant="ghost"
          onClick={() => setIsReportDialogOpen(true)}
          className="h-7 px-0 text-sm font-bold text-accent underline">
          Nahlásiť zmenu
        </Button>
      </div>

      <p className="text-base font-semibold">
        Pridávajte nových ambasádorov, sledujte ich výkonnosť a prehľad
        klientov, ktorých získali. Pre otázky alebo úpravy kontaktujte náš tím
        cez „Nahlásiť zmenu".
      </p>

      <div className="flex flex-col gap-4">
        <div className="flex flex-row justify-between items-center">
          <HeadingThree>Prehľad</HeadingThree>
          <Button
            variant="ghost"
            disabled={downloadingKey != null}
            onClick={() => setIsExportDialogOpen(true)}
            className="h-7">
            <span className="text-sm font-bold text-accent underline">
              Export XLSX
            </span>
          </Button>
        </div>

        <div className="flex flex-col">
          <div className="flex flex-row gap-10 p-5 bg-foreground">
            <span className="text-sm font-semibold text-white">
              Za celé obdobie
            </span>
          </div>
          <div className="grid grid-cols-2 border border-foreground divide-x div">
            <div className="flex flex-col justify-between gap-10 p-5">
              <div className="flex flex-row items-center gap-2">
                <GraphIcon className="text-foreground shrink-0" />
                <span className="text-base font-semibold text-foreground">
                  Získaný klienti
                </span>
              </div>
              <span className="text-[32px] font-semibold text-foreground self-end">
                {data?.sums.clientCount ?? 0}
              </span>
            </div>
            <div className="flex flex-col justify-between gap-10 p-5">
              <div className="flex flex-row items-center gap-2">
                <GraphIcon className="text-foreground shrink-0" />
                <span className="text-base font-semibold text-foreground">
                  Investované klientami
                </span>
              </div>
              <span className="text-[32px] font-semibold text-foreground self-end">
                <CurrencyFormatter
                  className="text-xl md:text-[32px] font-semibold"
                  decimalClassName="text-base md:text-xl font-semibold"
                  value={data?.sums.investedSum ?? 0}
                />{' '}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="flex flex-col gap-4">
        <div className="flex flex-row justify-between items-center">
          <HeadingThree>Moji ambasádori</HeadingThree>
          <Button
            className="h-[35px]"
            onClick={() => setIsCreateDialogOpen(true)}>
            <span className="text-sm font-medium">Vytvoriť nový kód</span>
          </Button>
        </div>

        <AmbassadorsTable
          items={items}
          isLoading={isLoading}
          onSelect={onSelectAmbassador}
        />
      </div>

      {data != null && firstMonth && (
        <ExportDialog
          open={isExportDialogOpen}
          onOpenChange={setIsExportDialogOpen}
          downloadingKey={downloadingKey}
          months={months}
          exportHandler={handleExportGlobal}
        />
      )}
    </>
  )
}
